import { Box, Stack } from '@chakra-ui/react';
import { get } from 'lodash';
import React, { useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { createStructuredSelector } from 'reselect';

import { GlobalReducerState } from 'app/reducers';
import { handleUnlockMainNav } from 'Containers/App/actions';
import {
  makeGetJvpHasPublicationWithFlag,
  makeSelectCommercialField,
  makeSelectConfigField,
} from 'Containers/App/selectors';
import {
  changeFormValue,
  recommendWithEphemeralPlan,
  selectHealthPlan,
} from 'Containers/CommercialRoutes/actions';
import {
  makeGetDisplayPdfLink,
  makeGetFamilyHsaContributionLimit,
  makeGetHdhpMinDeductible,
  makeGetHsaContributionLimit,
  makeGetIndividualHsaContributionLimit,
  makeGetRecommendationByPlanId,
} from 'Containers/CommercialRoutes/selectors';
import { EnteredPlan } from 'Containers/CommercialRoutes/types';
import {
  makeGetBuilderGoSetting,
  makeGetBuilderGoSettings,
  makeGetHasSpouse,
  makeGetHousehold,
  makeGetIsFamily,
} from 'Containers/ProfilePage/selectors';
import { NetworkTier } from 'Containers/ResultPage/CompareModal';
import { DRUG_TYPES, SERVICE_TYPES } from 'Containers/ResultPage/constants';
import { useFeatureFlagContext } from 'Contexts/featureFlagContext';
import { useProfileContext } from 'Contexts/profileContext';
import { Button, Paragraph } from 'DesignLibrary/atoms';
import { PageLayout } from 'DesignLibrary/atoms/PageLayout';
import Plan from 'Models/plan';
import Text from 'Shared/Text';
import { Household, Recommendation } from 'Types/entities';
import { BuilderGoSettings, PeopleLikeYou } from 'Utils/apiTypes';
import { ERROR_PATH, RESULT_PATH } from 'Utils/urls';

import { CallOutCard } from './CallOutCard';
import { CostBreakdownCard } from './CostBreakdownCard';
import { CtaCards } from './CtaCards';
import Disclaimers from './Disclaimers';
import { DoctorsProvidersCard } from './DoctorsProvidersCard';
import { DoctorVisitsCard } from './DoctorVisitsCard';
import { OtherNotesCard } from './OtherNotesCard';
import { PlanEligibility } from './PlanEligibility';
import { PlanEssentialsCard } from './PlanEssentialsCard';
import { PrescriptionsCard } from './PrescriptionsCard';
import { PricingOverview } from './PricingOverview';
import { Container, CtaCardsWrapper, Footnote, TopBar } from './styled';
import { ViewPlanPageHeader } from './ViewPlanPageHeader';

interface ViewPlanPageStateProps {
  client: string;
  isFamily: boolean;
  hasSpouse: boolean;
  hsaContributionLimit: number | null;
  selectedPlan: GlobalReducerState['commercialApp']['selectedHealthPlan'];
  searchProviderLinks: {
    [key: string]: string;
  } | null;
  maximumAvailableAnnualSihraContribution: number | null;
  individualHsaContributionLimit: number;
  familyHsaContributionLimit: number;
  enteredPlan: EnteredPlan;
  displayPdfButton: boolean;
  killMedRec: boolean;
  peopleLikeYou: PeopleLikeYou | Record<string, never>;
  builderGoSettings: BuilderGoSettings | null;
  household: Household;
  hdhpMinDeductible: number | null;
  showOonInPbw: boolean;
}

interface ViewPlanPageDispatchProps {
  selectPlan: (recommendation: Recommendation | Record<string, never>) => void;
  getRecommendationsWithEphemeralPlan: (enteredPlan: EnteredPlan) => void;
  saveEphemeralPlan: (plan: EnteredPlan) => void;
  lockFuturePages: () => void;
}

export type ViewPlanPageProps = ViewPlanPageStateProps & ViewPlanPageDispatchProps;

export const ViewPlanPage = ({
  selectedPlan,
  client,
  isFamily,
  hasSpouse,
  hsaContributionLimit,
  searchProviderLinks,
  maximumAvailableAnnualSihraContribution,
  individualHsaContributionLimit,
  familyHsaContributionLimit,
  enteredPlan,
  displayPdfButton,
  killMedRec,
  peopleLikeYou,
  selectPlan,
  getRecommendationsWithEphemeralPlan,
  saveEphemeralPlan,
  lockFuturePages,
  builderGoSettings,
  household,
  hdhpMinDeductible,
  showOonInPbw,
}: ViewPlanPageProps) => {
  const navigate = useNavigate();
  const params = useParams<{ planId: string }>();
  if (!params?.planId) {
    navigate(ERROR_PATH);
    return null;
  }

  const recommendation = useSelector(makeGetRecommendationByPlanId(params.planId));

  const plan = new Plan(recommendation);

  const { is_network_tier_toggle_enabled } = useFeatureFlagContext();

  const [selectedTier, setSelectedTier] = useState<NetworkTier>(() =>
    plan.hasMostPreferredNetwork ? 'most_preferred' : 'in_network',
  );
  const effectiveShowOonInPbw = is_network_tier_toggle_enabled ? showOonInPbw : false;
  const { selectedPublicationInfo } = useProfileContext();

  // display provider search section feature enabled from alex builder
  const docFinderEnabled = selectedPublicationInfo?.publication?.docfinder_enabled;

  // Plan properties
  const { planId } = plan;

  const handleSelectHealthPlan = () => {
    // set selected plan on Commercial reducer
    selectPlan(recommendation);
  };

  const handleDeselectHealthPlan = () => {
    // set deselected plan on Commercial reducer
    selectPlan({});
    lockFuturePages();
  };

  // TODO - Do we want to make this predicate a property on `Plan`?
  const isBuildAPlan = recommendation.plan.plan_type === 'Custom';

  const displayEditPlanButton = isBuildAPlan;

  const pdfButtonLink = recommendation.plan.benefits_summary_url || '';

  const displaySearchProviderButton = !isBuildAPlan && searchProviderLinks !== null;
  const searchProviderButtonLink = get(searchProviderLinks, planId, '');

  const planPdfButtonTextField = 'health_section.button_text.planPdf';
  const providerSearchButtonTextField = 'health_section.button_text.providerSearch';

  const getIsAnyCostSubjectToDeductible = () => {
    const serviceCostTypes = [SERVICE_TYPES.PRIMARY_CARE, SERVICE_TYPES.SPECIALIST_CARE];
    const drugCostTypes = [DRUG_TYPES.NON_FORMULARY_DRUG];
    // We don't display imputed benefits - Make sure we are only checking displayed benefits
    const isAnyDrugCostSubjectToDeductible = drugCostTypes.some(
      (i) => plan[i].deductible_applies && !plan[i].imputed,
    );
    const isAnyServiceCostSubjectToDeductible = serviceCostTypes.some((i) => plan[i].deductible_applies);
    return isAnyDrugCostSubjectToDeductible || isAnyServiceCostSubjectToDeductible;
  };

  const { is_no_deductible_plans_enabled } = useFeatureFlagContext();

  const isNoDeductibleCallOutAvailable = plan?.isNoDeductiblePlan && is_no_deductible_plans_enabled;

  return (
    <>
      <PageLayout>
        <TopBar>
          <Button
            className="back-to-results"
            iconLeft="ArrowLeft"
            buttonType="standalone"
            onClick={() => navigate(RESULT_PATH)}
          >
            <Text field="health_details_section.previous_page_button_text" />
          </Button>
        </TopBar>
      </PageLayout>
      <PageLayout bg="x" id="plan-detail-container">
        <Container>
          <section className="header-card">
            <ViewPlanPageHeader
              recommendation={recommendation}
              selectedPlan={selectedPlan}
              handleEnroll={handleSelectHealthPlan}
              handleDeselect={handleDeselectHealthPlan}
              maximumAvailableAnnualSihraContribution={maximumAvailableAnnualSihraContribution || 0}
              displayEditPlanButton={displayEditPlanButton}
              displayPdfButton={displayPdfButton && !isBuildAPlan}
              pdfButtonLink={pdfButtonLink}
              displaySearchProviderButton={displaySearchProviderButton}
              searchProviderButtonTextField={providerSearchButtonTextField}
              searchProviderButtonLink={searchProviderButtonLink}
              individualHsaContributionLimit={individualHsaContributionLimit}
              familyHsaContributionLimit={familyHsaContributionLimit}
              enteredPlan={enteredPlan}
              killMedRec={killMedRec}
              getRecommendationsWithEphemeralPlan={getRecommendationsWithEphemeralPlan}
              saveEphemeralPlan={saveEphemeralPlan}
              isFamily={isFamily}
              plyData={peopleLikeYou}
              selectedTier={selectedTier}
              setSelectedTier={setSelectedTier}
              showOonInPbw={effectiveShowOonInPbw}
              isNetworkTierToggleEnabled={is_network_tier_toggle_enabled ?? false}
            />
          </section>

          <Stack gap={2} className="plan-content">
            <Disclaimers client={client} planId={planId} hasSpouse={hasSpouse} />

            <PricingOverview
              recommendation={recommendation}
              hsaBalance={household?.hsa_current_balance_toward_oop}
              selectedTier={selectedTier}
            />

            {isNoDeductibleCallOutAvailable && (
              <CallOutCard recommendation={recommendation} type="no_deductible" />
            )}

            <PlanEligibility
              recommendation={recommendation}
              selectedPlan={selectedPlan}
              maximumAvailableAnnualSihraContribution={maximumAvailableAnnualSihraContribution || 0}
              individualHsaContributionLimit={individualHsaContributionLimit}
              familyHsaContributionLimit={familyHsaContributionLimit}
              hsaContributionLimit={hsaContributionLimit}
              builderGoSettings={builderGoSettings}
              hdhpMinDeductible={hdhpMinDeductible}
            />

            <PlanEssentialsCard
              recommendation={recommendation}
              selectedTier={selectedTier}
              isNetworkTierToggleEnabled={is_network_tier_toggle_enabled ?? false}
            />

            {docFinderEnabled && <DoctorsProvidersCard planId={planId} recommendation={recommendation} />}

            <DoctorVisitsCard client={client} recommendation={recommendation} selectedTier={selectedTier} />

            <PrescriptionsCard client={client} recommendation={recommendation} selectedTier={selectedTier} />

            {!isNoDeductibleCallOutAvailable && getIsAnyCostSubjectToDeductible() && (
              <Footnote>
                <Paragraph size="small" id="afterDeductible" aria-hidden>
                  *<Text field="health_details_section.after_deductible" />
                </Paragraph>
              </Footnote>
            )}

            <CostBreakdownCard
              recommendation={recommendation}
              hsaContributionLimit={hsaContributionLimit}
              hsaBalance={household?.hsa_current_balance_toward_oop}
              selectedTier={selectedTier}
            />

            <OtherNotesCard
              recommendation={recommendation}
              hsaContributionLimit={hsaContributionLimit || 0}
            />

            <CtaCardsWrapper>
              <CtaCards
                left={{
                  isVisible: displayPdfButton && !isBuildAPlan,
                  headerTextField: 'health_details_section.cta_pdf_header_text',
                  buttonTextField: planPdfButtonTextField,
                  buttonUrl: pdfButtonLink,
                  type: 'pdf',
                }}
                right={{
                  isVisible: displaySearchProviderButton,
                  headerTextField: 'health_details_section.cta_provider_header_text',
                  buttonTextField: providerSearchButtonTextField,
                  buttonUrl: searchProviderButtonLink,
                  type: 'provider',
                }}
              />
            </CtaCardsWrapper>
          </Stack>
        </Container>
      </PageLayout>
      <PageLayout>
        <Box pt={10} pb={24} className="bottom-button">
          <Button
            className="back-to-results"
            iconLeft="ArrowLeft"
            buttonType="standalone"
            onClick={() => navigate(RESULT_PATH)}
          >
            <Text field="health_details_section.previous_page_button_text" />
          </Button>
        </Box>
      </PageLayout>
    </>
  );
};

const mapStateToProps = () =>
  createStructuredSelector<GlobalReducerState, ViewPlanPageStateProps>({
    client: makeSelectConfigField('client'),
    isFamily: makeGetIsFamily(),
    hasSpouse: makeGetHasSpouse(),
    hsaContributionLimit: makeGetHsaContributionLimit(),
    maximumAvailableAnnualSihraContribution: makeSelectCommercialField('annual_sihra_contribution'),
    searchProviderLinks: makeSelectConfigField('provider_search_links'),
    selectedPlan: makeSelectCommercialField('selectedHealthPlan'),
    individualHsaContributionLimit: makeGetIndividualHsaContributionLimit(),
    familyHsaContributionLimit: makeGetFamilyHsaContributionLimit(),
    enteredPlan: makeSelectCommercialField('entered_plan'),
    displayPdfButton: makeGetDisplayPdfLink(),
    killMedRec: makeGetBuilderGoSetting('kill_med_rec'),
    peopleLikeYou: makeSelectCommercialField('people_like_you'),
    builderGoSettings: makeGetBuilderGoSettings(),
    household: makeGetHousehold(),
    hdhpMinDeductible: makeGetHdhpMinDeductible(),
    showOonInPbw: makeGetJvpHasPublicationWithFlag('show_oon_in_pbw'),
  });

export function mapDispatchToProps(dispatch): ViewPlanPageDispatchProps {
  return {
    selectPlan: (plan) => dispatch(selectHealthPlan(plan)),
    getRecommendationsWithEphemeralPlan: (plan) => dispatch(recommendWithEphemeralPlan(plan)),
    saveEphemeralPlan: (plan) => dispatch(changeFormValue(plan, 'entered_plan')),
    lockFuturePages: () => dispatch(handleUnlockMainNav(RESULT_PATH)),
  };
}

const withConnect = connect(mapStateToProps, mapDispatchToProps);

export default withConnect(ViewPlanPage);
